import JSZip from "jszip";
import { jsPDF } from "jspdf";
import { markdownToHtml } from "@/shared/lib/markdown";
import { fetchJobSubtitles, fetchReportDocx, reportDownload, type PromptReportType } from "./api";
import robotoRegularBase64 from "@/fonts/robotoRegular";
import robotoBoldBase64 from "@/fonts/robotoBold";

/**
 * Hand a blob to the user, and tell the backend which format left the product.
 *
 * Every download in this module funnels through here, which is why the reporting lives here
 * and not at nine call sites. The two server-rendered paths — the DOCX export and the
 * subtitle endpoint — already emit their own event API-side, so they pass
 * `alreadyCounted: true`; without that they would appear twice and the ranking would be wrong
 * in the direction that flatters whatever the server happens to render.
 */
function triggerDownload(
  blob: Blob,
  filename: string,
  options?: { alreadyCounted?: boolean; jobId?: number; reportType?: string },
): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  if (!options?.alreadyCounted) {
    const format = filename.split(".").pop()?.toLowerCase() ?? "";
    if (format) {
      void reportDownload(format, { jobId: options?.jobId, reportType: options?.reportType });
    }
  }
}

/** Fetch a report's server-rendered .docx (JWT auth) and save it to disk. */
export async function downloadReportDocx(reportId: number): Promise<void> {
  const { blob, filename } = await fetchReportDocx(reportId);
  triggerDownload(blob, filename, { alreadyCounted: true });
}

/** Fetch a job's server-generated subtitles (SRT/VTT/ASS/TXT, enriched speaker
 *  names, real timestamps) and save them to disk. */
export async function downloadJobSubtitles(jobId: number, fmt: string): Promise<void> {
  const { blob, filename } = await fetchJobSubtitles(jobId, fmt);
  triggerDownload(blob, filename, { alreadyCounted: true });
}

export function downloadAsText(content: string, filename: string): void {
  triggerDownload(new Blob([content], { type: "text/plain" }), filename);
}

export function downloadAsMarkdown(content: string, filename: string): void {
  triggerDownload(new Blob([content], { type: "text/markdown" }), filename);
}

/**
 * Export content as PDF via browser print dialog.
 * Reuses the same approach as exportTranscriptToPdf in ReportPage.
 */
export function downloadAsPdf(markdownContent: string, title: string): void {
  const htmlBody = markdownToHtml(markdownContent);

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${escapeHtml(title)}</title>
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        margin: 40px;
        color: #1a1a1a;
        line-height: 1.6;
      }
      h1 { font-size: 22px; margin-bottom: 8px; }
      h2 { font-size: 18px; margin-top: 24px; }
      h3 { font-size: 15px; margin-top: 20px; }
      p { margin: 8px 0; }
      ul, ol { margin: 8px 0; padding-left: 24px; }
      li { margin: 4px 0; }
      blockquote {
        border-left: 3px solid #d1d5db;
        margin: 12px 0;
        padding: 4px 16px;
        color: #4b5563;
      }
      code {
        background: #f3f4f6;
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 0.9em;
      }
      hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 12px 0;
        font-size: 13px;
        table-layout: fixed;
      }
      th, td {
        border: 1px solid #e5e7eb;
        padding: 6px 8px;
        text-align: left;
        vertical-align: top;
        word-wrap: break-word;
      }
      th { background: #f9fafb; font-weight: 600; }
      @media print { body { margin: 20px; } }
    </style>
  </head><body>
    <h1>${escapeHtml(title)}</h1>
    ${htmlBody}
    <script>window.onafterprint=()=>window.close();window.print();<\/script>
  </body></html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

/**
 * Convert transcript markdown content to SRT format.
 * Parses lines like "00:00:05 Speaker Name: text" or markdown table rows.
 */
export function downloadAsSrt(content: string, filename: string): void {
  const lines = content.split("\n");
  const entries: { index: number; startTime: string; text: string }[] = [];
  let counter = 0;

  for (const line of lines) {
    // Match patterns like "00:00:05" or "00:05" at start, followed by speaker/text
    const match = line.match(/^[\s*|-]*(\d{1,2}:\d{2}(?::\d{2})?)\s+(.+)/);
    if (match) {
      counter++;
      const rawTime = match[1];
      const text = match[2]
        .replace(/^\*\*(.+?)\*\*:?\s*/, "$1: ") // bold speaker names
        .replace(/\|/g, "")
        .trim();

      entries.push({
        index: counter,
        startTime: toSrtTimestamp(rawTime),
        text,
      });
    }
  }

  // Build SRT with estimated end times (each entry lasts until next one starts)
  const srtLines: string[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const endTime =
      i + 1 < entries.length
        ? entries[i + 1].startTime
        : offsetSrtTimestamp(entry.startTime, 3);
    srtLines.push(`${entry.index}`);
    srtLines.push(`${entry.startTime} --> ${endTime}`);
    srtLines.push(entry.text);
    srtLines.push("");
  }

  if (srtLines.length === 0) {
    // Fallback: just download as plain text if parsing fails
    downloadAsText(content, filename.replace(/\.srt$/, ".txt"));
    return;
  }

  triggerDownload(
    new Blob([srtLines.join("\n")], { type: "application/x-subrip" }),
    filename,
  );
}

function toSrtTimestamp(time: string): string {
  // Convert "MM:SS" or "HH:MM:SS" to "HH:MM:SS,000"
  const parts = time.split(":");
  if (parts.length === 2) {
    return `00:${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")},000`;
  }
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}:${parts[2].padStart(2, "0")},000`;
}

function offsetSrtTimestamp(timestamp: string, seconds: number): string {
  const [timePart] = timestamp.split(",");
  const parts = timePart.split(":").map(Number);
  let totalSec = parts[0] * 3600 + parts[1] * 60 + parts[2] + seconds;
  const h = Math.floor(totalSec / 3600);
  totalSec %= 3600;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")},000`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Strip markdown formatting to get plain text.
 */
function stripMarkdown(content: string): string {
  return content
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/^>\s?/gm, "")
    .replace(/^[-*+]\s+/gm, "• ")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/^---+$/gm, "");
}

export type DownloadFormat = "pdf" | "docx" | "txt" | "srt" | "md" | "vtt" | "ass";

export interface ReportForDownload {
  type: PromptReportType;
  label: string;
  content: string;
}

/**
 * Register Roboto font (with Cyrillic support) in a jsPDF instance.
 */
function registerRoboto(doc: jsPDF): void {
  doc.addFileToVFS("Roboto-Regular.ttf", robotoRegularBase64);
  doc.addFileToVFS("Roboto-Bold.ttf", robotoBoldBase64);
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
}

// ─── PDF rendering ────────────────────────────────────────────────────────────

const PDF_COLORS = {
  // New palette
  headerBg: [27, 42, 74] as const,           // #1B2A4A
  headerSubtitle: [139, 164, 204] as const,  // #8BA4CC
  white: [255, 255, 255] as const,
  text: [51, 51, 51] as const,               // #333333
  sectionHeader: [136, 136, 136] as const,   // #888888
  accentPurple: [83, 74, 183] as const,      // #534AB7
  quoteBg: [248, 247, 255] as const,         // #F8F7FF
  cardBg: [245, 245, 242] as const,          // #F5F5F2
  badgeBlueBg: [230, 241, 251] as const,     // #E6F1FB
  badgeBlueText: [12, 68, 124] as const,     // #0C447C
  badgePurpleBg: [238, 237, 254] as const,   // #EEEDFE
  badgePurpleText: [60, 52, 137] as const,   // #3C3489
  badgeGreenBg: [234, 243, 222] as const,    // #EAF3DE
  badgeGreenText: [39, 80, 10] as const,     // #27500A

  // Legacy (kept for code blocks, links, tables — not part of redesign)
  muted: [136, 136, 136] as const,
  heading: [17, 24, 39] as const,
  rule: [220, 220, 220] as const,
  codeBg: [243, 244, 246] as const,
  codeText: [55, 65, 81] as const,
  quoteBar: [83, 74, 183] as const,
  quoteText: [60, 52, 137] as const,
  link: [37, 99, 235] as const,
  tableHeaderBg: [249, 250, 251] as const,
  tableBorder: [229, 231, 235] as const,
};

// Layout constants (mm)
const HEADER_BAND_H = 22;
const FOOTER_BAND_H = 12;
const PAGE_MARGIN = 18;

interface InlineRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  link?: string;
}

interface RenderCtx {
  doc: jsPDF;
  margin: number;
  pageW: number;
  pageH: number;
  contentBottom: number; // y limit before forcing page break
  baseSize: number;
  lineGap: number; // mm between baselines at base size
  y: number;
}

type Block =
  | { kind: "p"; inline: InlineRun[] }
  | { kind: "h"; level: number; inline: InlineRun[] }
  | { kind: "list"; ordered: boolean; items: ListItem[] }
  | { kind: "quote"; lines: InlineRun[][] }
  | { kind: "code"; text: string }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | { kind: "hr" };

interface ListItem {
  inline: InlineRun[];
  children?: Block[];
}

/**
 * Parse markdown into a flat list of blocks. Handles headings, paragraphs,
 * fenced code blocks, blockquotes, ordered/unordered lists (with simple
 * one-level nesting), markdown pipe tables, and horizontal rules.
 */
function parseBlocks(md: string): Block[] {
  const rawLines = md.replace(/\r\n?/g, "\n").split("\n");
  // Fix 5: merge a lone "N." marker line with the following content line so
  // "1.\nText" renders as one ordered-list item ("1. Text") rather than a bare
  // number followed by an orphan paragraph.
  const lines: string[] = [];
  for (let j = 0; j < rawLines.length; j++) {
    if (rawLines[j].match(/^\s*\d+\.\s*$/) && j + 1 < rawLines.length && rawLines[j + 1].trim()) {
      lines.push(rawLines[j].trimEnd() + " " + rawLines[j + 1].trimStart());
      j++;
    } else {
      lines.push(rawLines[j]);
    }
  }
  const blocks: Block[] = [];
  let i = 0;

  const isHr = (s: string) => /^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(s);
  const isTableSep = (s: string) =>
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(s);
  const splitRow = (s: string) =>
    s
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      .split("|")
      .map((c) => c.trim());

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.replace(/\s+$/, "");

    if (line.trim() === "") {
      i++;
      continue;
    }

    // Fix 1: drop lone dash artifacts ("-", em/en dash) that aren't real content.
    if (line.trim() === "-" || line.trim() === "—" || line.trim() === "–") {
      i++;
      continue;
    }

    // Fenced code block
    const fence = line.match(/^\s*```/);
    if (fence) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // skip closing fence
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (h) {
      blocks.push({
        kind: "h",
        level: h[1].length,
        inline: parseInline(h[2]),
      });
      i++;
      continue;
    }

    // Horizontal rule
    if (isHr(line)) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }

    // Pipe table (need at least header + separator)
    if (
      /^\s*\|.+\|/.test(line) &&
      i + 1 < lines.length &&
      isTableSep(lines[i + 1])
    ) {
      const headers = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push({ kind: "table", headers, rows });
      continue;
    }

    // Blockquote
    if (/^\s*>/.test(line)) {
      const quoted: InlineRun[][] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        const text = lines[i].replace(/^\s*>\s?/, "");
        quoted.push(parseInline(text));
        i++;
      }
      blocks.push({ kind: "quote", lines: quoted });
      continue;
    }

    // List (unordered or ordered)
    const listStart =
      line.match(/^(\s*)([-*+])\s+(.+)$/) ||
      line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (listStart) {
      const ordered = /\d/.test(listStart[2]);
      const items: ListItem[] = [];
      const baseIndent = listStart[1].length;

      while (i < lines.length) {
        const cur = lines[i];
        const m =
          cur.match(/^(\s*)([-*+])\s+(.+)$/) ||
          cur.match(/^(\s*)(\d+)\.\s+(.+)$/);
        if (!m) break;
        const curIndent = m[1].length;
        const curOrdered = /\d/.test(m[2]);
        if (curIndent !== baseIndent || curOrdered !== ordered) break;

        const itemLines: string[] = [m[3]];
        i++;
        // Continuation lines: blank-line-terminated, indented further than baseIndent
        while (i < lines.length) {
          const next = lines[i];
          if (next.trim() === "") break;
          const nestedMatch =
            next.match(/^(\s*)([-*+])\s+(.+)$/) ||
            next.match(/^(\s*)(\d+)\.\s+(.+)$/);
          if (nestedMatch) {
            // Sibling list item at same indent ends the current item
            if (nestedMatch[1].length <= baseIndent) break;
          }
          // Wrapped paragraph line at deeper indent — keep
          if (next.match(/^\s+\S/) || next.match(/^\S/)) {
            itemLines.push(next.replace(/^\s+/, ""));
            i++;
          } else {
            break;
          }
        }

        items.push({ inline: parseInline(itemLines.join(" ")) });
      }

      blocks.push({ kind: "list", ordered, items });
      continue;
    }

    // Paragraph: collect consecutive non-empty, non-special lines
    const paraLines: string[] = [line];
    i++;
    while (i < lines.length) {
      const next = lines[i];
      if (
        next.trim() === "" ||
        /^(\s*)([-*+]|\d+\.)\s+/.test(next) ||
        /^\s*>/.test(next) ||
        /^\s*```/.test(next) ||
        /^#{1,6}\s/.test(next) ||
        isHr(next) ||
        /^\s*\|.+\|/.test(next)
      ) {
        break;
      }
      paraLines.push(next);
      i++;
    }
    blocks.push({ kind: "p", inline: parseInline(paraLines.join(" ")) });
  }

  return blocks;
}

/**
 * Parse a single line of markdown into styled inline runs.
 * Supports **bold**, *italic*, `code`, [text](url). Greedy left-to-right.
 */
function parseInline(text: string): InlineRun[] {
  const runs: InlineRun[] = [];
  let buf = "";
  let i = 0;

  const flushBuf = () => {
    if (buf) {
      runs.push({ text: buf });
      buf = "";
    }
  };

  while (i < text.length) {
    const rest = text.slice(i);
    let m: RegExpMatchArray | null;

    if ((m = rest.match(/^\*\*\*(.+?)\*\*\*/))) {
      flushBuf();
      runs.push({ text: m[1], bold: true, italic: true });
      i += m[0].length;
    } else if ((m = rest.match(/^\*\*(.+?)\*\*/))) {
      flushBuf();
      runs.push({ text: m[1], bold: true });
      i += m[0].length;
    } else if ((m = rest.match(/^__(.+?)__/))) {
      flushBuf();
      runs.push({ text: m[1], bold: true });
      i += m[0].length;
    } else if ((m = rest.match(/^\*([^*\n]+)\*/))) {
      flushBuf();
      runs.push({ text: m[1], italic: true });
      i += m[0].length;
    } else if ((m = rest.match(/^_([^_\n]+)_/))) {
      flushBuf();
      runs.push({ text: m[1], italic: true });
      i += m[0].length;
    } else if ((m = rest.match(/^`([^`]+)`/))) {
      flushBuf();
      runs.push({ text: m[1], code: true });
      i += m[0].length;
    } else if ((m = rest.match(/^\[([^\]]+)\]\(([^)]+)\)/))) {
      flushBuf();
      runs.push({ text: m[1], link: m[2] });
      i += m[0].length;
    } else {
      buf += text[i];
      i++;
    }
  }
  flushBuf();
  return runs;
}

function applyRunStyle(
  doc: jsPDF,
  run: InlineRun | { code?: boolean; bold?: boolean; italic?: boolean; link?: string },
  size: number,
): void {
  doc.setFontSize(size);
  if (run.code) {
    doc.setFont("Roboto", "normal");
    doc.setTextColor(...PDF_COLORS.codeText);
  } else if (run.link) {
    doc.setFont("Roboto", run.bold ? "bold" : "normal");
    doc.setTextColor(...PDF_COLORS.link);
  } else if (run.bold) {
    doc.setFont("Roboto", "bold");
    doc.setTextColor(...PDF_COLORS.text);
  } else {
    doc.setFont("Roboto", "normal");
    doc.setTextColor(...PDF_COLORS.text);
  }
}

/**
 * Render a sequence of inline runs with word-wrapping and per-run styling.
 * Returns the new y after rendering.
 */
function renderInline(
  ctx: RenderCtx,
  runs: InlineRun[],
  startX: number,
  width: number,
  size: number,
  lineHeight: number,
  textColor: readonly [number, number, number] = PDF_COLORS.text,
): void {
  const { doc } = ctx;
  let x = startX;
  const right = startX + width;

  // Tokenize each run into [word, trailingSpace] pairs while preserving runs across boundaries.
  type Token = { run: InlineRun; word: string; space: string };
  const tokens: Token[] = [];
  for (const run of runs) {
    if (!run.text) continue;
    // Split keeping spaces. Using a regex that pairs non-space + trailing space.
    const parts = run.text.match(/\S+\s*|\s+/g) ?? [];
    for (const part of parts) {
      const m = part.match(/^(\S*)(\s*)$/);
      if (!m) continue;
      tokens.push({ run, word: m[1], space: m[2] });
    }
  }

  let lineFirst = true;

  const newline = () => {
    x = startX;
    ctx.y += lineHeight;
    ensureSpace(ctx, lineHeight);
    lineFirst = true;
  };

  for (const tok of tokens) {
    if (tok.word === "") {
      // Pure whitespace token (line break inside markdown collapsed to space).
      if (!lineFirst) {
        applyRunStyle(doc, tok.run, size);
        if (!tok.run.code && !tok.run.link) doc.setTextColor(...textColor);
        const sw = doc.getTextWidth(" ");
        if (x + sw > right) {
          newline();
        } else {
          x += sw;
        }
      }
      continue;
    }

    applyRunStyle(doc, tok.run, size);
    if (!tok.run.code && !tok.run.link) doc.setTextColor(...textColor);

    const ww = doc.getTextWidth(tok.word);
    if (!lineFirst && x + ww > right) {
      newline();
      applyRunStyle(doc, tok.run, size);
      if (!tok.run.code && !tok.run.link) doc.setTextColor(...textColor);
    }

    if (tok.run.code) {
      // Light gray background pill behind the word.
      const pad = 0.6;
      const h = lineHeight * 0.78;
      doc.setFillColor(...PDF_COLORS.codeBg);
      doc.roundedRect(x - pad, ctx.y - h * 0.78, ww + pad * 2, h, 0.6, 0.6, "F");
      applyRunStyle(doc, tok.run, size);
    }

    doc.text(tok.word, x, ctx.y);

    if (tok.run.link) {
      // Subtle underline.
      doc.setDrawColor(...PDF_COLORS.link);
      doc.setLineWidth(0.15);
      doc.line(x, ctx.y + 0.6, x + ww, ctx.y + 0.6);
    }

    x += ww;
    lineFirst = false;

    if (tok.space) {
      const sw = doc.getTextWidth(" ");
      if (x + sw > right) {
        newline();
      } else {
        x += sw;
      }
    }
  }
}

function ensureSpace(ctx: RenderCtx, needed: number): void {
  if (ctx.y + needed > ctx.contentBottom) {
    ctx.doc.addPage();
    ctx.y = ctx.margin;
  }
}

function renderHeading(ctx: RenderCtx, level: number, runs: InlineRun[]): void {
  // h1 — title-style (bold black, larger). h2/h3 — uppercase gray section headers
  // with letter-spacing. h4+ — small bold body-color subheads.
  if (level === 1) {
    const size = 16;
    const lineH = size * 0.45 + 1.5;
    ctx.y += 4;
    ensureSpace(ctx, lineH + 3);
    const boldRuns = runs.map((r) => ({ ...r, bold: true }));
    renderInline(ctx, boldRuns, ctx.margin, ctx.pageW - ctx.margin * 2, size, lineH, [0, 0, 0]);
    ctx.y += 3;
    return;
  }

  if (level === 2 || level === 3) {
    const size = 11;
    const lineH = size * 0.42 + 1.4;
    ctx.y += level === 2 ? 4 : 3;
    const text = runs.map((r) => r.text).join("").toUpperCase();
    ctx.doc.setFont("Roboto", "bold");
    ctx.doc.setFontSize(size);
    ctx.doc.setTextColor(...PDF_COLORS.sectionHeader);
    const lines = wrapSpaced(ctx.doc, text, ctx.pageW - ctx.margin * 2, 0.5);
    ensureSpace(ctx, lineH * lines.length + 3);
    for (const line of lines) {
      drawSpacedText(ctx.doc, line, ctx.margin, ctx.y, 0.5);
      ctx.y += lineH;
    }
    ctx.y += 2;
    return;
  }

  // level 4..6: small bold subhead in body color
  const size = level === 4 ? 11 : 10;
  const lineH = size * 0.42 + 1.2;
  ctx.y += 2.5;
  ensureSpace(ctx, lineH + 2);
  const boldRuns = runs.map((r) => ({ ...r, bold: true }));
  renderInline(ctx, boldRuns, ctx.margin, ctx.pageW - ctx.margin * 2, size, lineH, PDF_COLORS.text);
  ctx.y += 1.5;
}

/** Draw text with manual letter-spacing (mm). Useful for uppercase section labels. */
function drawSpacedText(doc: jsPDF, text: string, x: number, y: number, spacingMm: number): void {
  let cx = x;
  for (const ch of text) {
    doc.text(ch, cx, y);
    cx += doc.getTextWidth(ch) + spacingMm;
  }
}

/** Word-wrap a string under a max width assuming `spacingMm` letter-spacing
 *  is applied to every char (matches `drawSpacedText`). */
function wrapSpaced(doc: jsPDF, text: string, maxWidth: number, spacingMm: number): string[] {
  const measureWord = (w: string): number => {
    let total = 0;
    for (const ch of w) total += doc.getTextWidth(ch) + spacingMm;
    return Math.max(0, total - spacingMm);
  };
  const spaceW = doc.getTextWidth(" ") + spacingMm;
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur: string[] = [];
  let curW = 0;
  for (const w of words) {
    const ww = measureWord(w);
    const needed = cur.length === 0 ? ww : curW + spaceW + ww;
    if (needed <= maxWidth || cur.length === 0) {
      cur.push(w);
      curW = needed;
    } else {
      lines.push(cur.join(" "));
      cur = [w];
      curW = ww;
    }
  }
  if (cur.length) lines.push(cur.join(" "));
  return lines;
}

function renderParagraph(ctx: RenderCtx, runs: InlineRun[]): void {
  const size = ctx.baseSize;
  const lineH = ctx.lineGap;
  ensureSpace(ctx, lineH);
  renderInline(ctx, runs, ctx.margin, ctx.pageW - ctx.margin * 2, size, lineH, PDF_COLORS.text);
  // 8px-ish trailing gap so structured `**Label:**`-style paragraphs separate
  // visually rather than running together.
  ctx.y += lineH * 0.7 + 2.2;
}

function renderList(ctx: RenderCtx, ordered: boolean, items: ListItem[]): void {
  const size = ctx.baseSize;
  const lineH = ctx.lineGap;
  const accentW = 1; // mm — width of the purple accent line (unordered only)
  const gap = 3;     // mm — gap between marker/accent line and text

  // Fix 4: ordered lists indent past the numeric marker (no accent bar);
  // unordered lists indent past the purple accent bar.
  ctx.doc.setFont("Roboto", "bold");
  ctx.doc.setFontSize(size);
  const numberWidth = ordered ? ctx.doc.getTextWidth(`${items.length}.`) : 0;
  const indent = ordered ? numberWidth + gap : accentW + gap;
  const innerWidth = ctx.pageW - ctx.margin * 2 - indent;

  items.forEach((item, idx) => {
    // Fix 3: skip empty list items (e.g. a stray "-" that parsed to a blank item).
    if (
      !item.inline ||
      item.inline.length === 0 ||
      (item.inline.length === 1 && !item.inline[0].text.trim())
    ) {
      return;
    }

    ensureSpace(ctx, lineH);
    const startY = ctx.y - lineH * 0.7;

    if (ordered) {
      // For ordered lists draw a numeric marker (no accent bar).
      ctx.doc.setFont("Roboto", "bold");
      ctx.doc.setFontSize(size);
      ctx.doc.setTextColor(...PDF_COLORS.accentPurple);
      ctx.doc.text(`${idx + 1}.`, ctx.margin, ctx.y);
    }

    renderInline(ctx, item.inline, ctx.margin + indent, innerWidth, size, lineH, PDF_COLORS.text);

    // Fix 4: draw the purple accent bar for unordered lists only.
    if (!ordered) {
      const endY = ctx.y + lineH * 0.3;
      ctx.doc.setFillColor(...PDF_COLORS.accentPurple);
      ctx.doc.rect(ctx.margin, startY, accentW, Math.max(lineH * 0.9, endY - startY), "F");
    }

    // ~2.8mm gap (≈8px) between items so structured fields like
    // "**Who:** value" / "**What:** value" don't overlap.
    ctx.y += lineH * 0.55 + 2.4;
  });
  ctx.y += 2;
}

function renderQuote(ctx: RenderCtx, lines: InlineRun[][]): void {
  const size = ctx.baseSize;
  const lineH = ctx.lineGap;
  const labelSize = 8.5;
  const labelLineH = labelSize * 0.42 + 2;
  const padX = 4;
  const padY = 3;
  const barW = 1; // mm
  const indent = padX + barW;
  const innerWidth = ctx.pageW - ctx.margin * 2 - indent - padX;

  // 1. Pre-measure: flatten all quote lines into wrapped strings.
  ctx.doc.setFont("Roboto", "normal");
  ctx.doc.setFontSize(size);
  const wrapped: string[] = [];
  for (const runs of lines) {
    const text = runs.map((r) => r.text).join("").trim();
    if (!text) continue;
    const ws = ctx.doc.splitTextToSize(text, innerWidth) as string[];
    wrapped.push(...ws);
  }
  if (wrapped.length === 0) return;

  const totalH = padY + labelLineH + wrapped.length * lineH + padY;

  // Try to keep the whole quote on one page; if it doesn't fit, push to the
  // next page rather than splitting (cleaner visually).
  ensureSpace(ctx, totalH + 2);

  // 2. Draw background pill + left accent bar.
  const startY = ctx.y - lineH * 0.65;
  ctx.doc.setFillColor(...PDF_COLORS.quoteBg);
  ctx.doc.roundedRect(
    ctx.margin,
    startY,
    ctx.pageW - ctx.margin * 2,
    totalH,
    1.4,
    1.4,
    "F",
  );
  ctx.doc.setFillColor(...PDF_COLORS.accentPurple);
  ctx.doc.rect(ctx.margin, startY, barW, totalH, "F");

  // 3. Draw label.
  ctx.doc.setFont("Roboto", "bold");
  ctx.doc.setFontSize(labelSize);
  ctx.doc.setTextColor(...PDF_COLORS.accentPurple);
  drawSpacedText(
    ctx.doc,
    "KEY QUOTE",
    ctx.margin + indent,
    startY + padY + labelSize * 0.42,
    0.4,
  );

  // 4. Draw body lines (italic-ish — we don't ship Roboto italic, so plain).
  ctx.doc.setFont("Roboto", "normal");
  ctx.doc.setFontSize(size);
  ctx.doc.setTextColor(...PDF_COLORS.quoteText);
  let yy = startY + padY + labelLineH + lineH * 0.7;
  for (const line of wrapped) {
    ctx.doc.text(line, ctx.margin + indent, yy);
    yy += lineH;
  }

  ctx.y = startY + totalH + 3;
}

/**
 * "The Gist" callout — a light primary-tinted box with a small "THE GIST" label
 * and the plain-language brief in normal body text. Mirrors the in-app card,
 * adapted for print. Rendered at the top of Summary PDFs only.
 */
function renderGistCallout(ctx: RenderCtx, text: string): void {
  const clean = text.trim();
  if (!clean) return;

  const size = ctx.baseSize;
  const lineH = ctx.lineGap;
  const labelSize = 8.5;
  const labelLineH = labelSize * 0.42 + 2;
  const padX = 4;
  const padY = 3;
  const innerWidth = ctx.pageW - ctx.margin * 2 - padX * 2;

  ctx.doc.setFont("Roboto", "normal");
  ctx.doc.setFontSize(size);
  const wrapped = ctx.doc.splitTextToSize(clean, innerWidth) as string[];
  if (wrapped.length === 0) return;

  const totalH = padY + labelLineH + wrapped.length * lineH + padY;
  ensureSpace(ctx, totalH + 3);

  const startY = ctx.y - lineH * 0.65;
  const boxW = ctx.pageW - ctx.margin * 2;

  // Light primary-tinted fill + soft primary border (≈ bg-primary/5 + border-primary/20).
  ctx.doc.setFillColor(...PDF_COLORS.quoteBg);
  ctx.doc.setDrawColor(...PDF_COLORS.accentPurple);
  ctx.doc.setLineWidth(0.2);
  ctx.doc.roundedRect(ctx.margin, startY, boxW, totalH, 1.6, 1.6, "FD");

  // Label.
  ctx.doc.setFont("Roboto", "bold");
  ctx.doc.setFontSize(labelSize);
  ctx.doc.setTextColor(...PDF_COLORS.accentPurple);
  drawSpacedText(ctx.doc, "THE GIST", ctx.margin + padX, startY + padY + labelSize * 0.42, 0.4);

  // Body (normal weight, standard text colour).
  ctx.doc.setFont("Roboto", "normal");
  ctx.doc.setFontSize(size);
  ctx.doc.setTextColor(...PDF_COLORS.text);
  let yy = startY + padY + labelLineH + lineH * 0.7;
  for (const line of wrapped) {
    ctx.doc.text(line, ctx.margin + padX, yy);
    yy += lineH;
  }

  ctx.y = startY + totalH + 4;
}

function renderCodeBlock(ctx: RenderCtx, text: string): void {
  const size = ctx.baseSize - 1;
  const lineH = size * 0.45 + 1.2;
  const padding = 2.5;
  const lines = text.split("\n");
  const blockWidth = ctx.pageW - ctx.margin * 2;

  ctx.doc.setFont("Roboto", "normal");
  ctx.doc.setFontSize(size);

  let i = 0;
  while (i < lines.length) {
    // Render up to one page worth at a time
    const startY = ctx.y;
    ensureSpace(ctx, lineH * 2);
    const available = ctx.contentBottom - ctx.y - padding * 2;
    const fits = Math.max(1, Math.floor(available / lineH));
    const chunk = lines.slice(i, i + fits);
    const blockH = chunk.length * lineH + padding * 2;

    ctx.doc.setFillColor(...PDF_COLORS.codeBg);
    ctx.doc.roundedRect(ctx.margin, startY - lineH * 0.7, blockWidth, blockH, 1.2, 1.2, "F");

    ctx.doc.setTextColor(...PDF_COLORS.codeText);
    let yy = startY + padding * 0.4;
    for (const ln of chunk) {
      const wrapped = ctx.doc.splitTextToSize(ln || " ", blockWidth - padding * 2);
      for (const w of wrapped) {
        ctx.doc.text(w, ctx.margin + padding, yy);
        yy += lineH;
      }
    }
    ctx.y = yy + padding - lineH * 0.4;
    i += chunk.length;
  }
  ctx.y += 2;
}

function renderHr(ctx: RenderCtx): void {
  ensureSpace(ctx, 6);
  ctx.y += 2;
  ctx.doc.setDrawColor(...PDF_COLORS.rule);
  ctx.doc.setLineWidth(0.3);
  ctx.doc.line(ctx.margin, ctx.y, ctx.pageW - ctx.margin, ctx.y);
  ctx.y += 4;
}

function renderTable(ctx: RenderCtx, headers: string[], rows: string[][]): void {
  const size = ctx.baseSize - 0.5;
  const lineH = size * 0.42 + 1.4;
  const cellPadX = 2;
  const cellPadY = 1.4;
  const totalW = ctx.pageW - ctx.margin * 2;
  const colCount = Math.max(headers.length, ...rows.map((r) => r.length), 1);
  const colWidth = totalW / colCount;

  const drawRow = (cells: string[], bold: boolean, fill: boolean) => {
    const wrappedCells = cells.map((c) => {
      ctx.doc.setFont("Roboto", bold ? "bold" : "normal");
      ctx.doc.setFontSize(size);
      return ctx.doc.splitTextToSize(c || " ", colWidth - cellPadX * 2) as string[];
    });
    const rowH = Math.max(...wrappedCells.map((w) => w.length)) * lineH + cellPadY * 2;
    ensureSpace(ctx, rowH + 2);

    if (fill) {
      ctx.doc.setFillColor(...PDF_COLORS.tableHeaderBg);
      ctx.doc.rect(ctx.margin, ctx.y - lineH * 0.7, totalW, rowH, "F");
    }

    ctx.doc.setDrawColor(...PDF_COLORS.tableBorder);
    ctx.doc.setLineWidth(0.2);

    for (let c = 0; c < colCount; c++) {
      const cellX = ctx.margin + c * colWidth;
      ctx.doc.rect(cellX, ctx.y - lineH * 0.7, colWidth, rowH);
      ctx.doc.setFont("Roboto", bold ? "bold" : "normal");
      ctx.doc.setFontSize(size);
      ctx.doc.setTextColor(...PDF_COLORS.text);
      const wrapped = wrappedCells[c] ?? [];
      let yy = ctx.y + cellPadY * 0.2;
      for (const w of wrapped) {
        ctx.doc.text(w, cellX + cellPadX, yy);
        yy += lineH;
      }
    }
    ctx.y += rowH;
  };

  drawRow(headers, true, true);
  for (const row of rows) drawRow(row, false, false);
  ctx.y += 2;
}

function renderBlock(ctx: RenderCtx, block: Block): void {
  switch (block.kind) {
    case "h":
      return renderHeading(ctx, block.level, block.inline);
    case "p":
      return renderParagraph(ctx, block.inline);
    case "list":
      return renderList(ctx, block.ordered, block.items);
    case "quote":
      return renderQuote(ctx, block.lines);
    case "code":
      return renderCodeBlock(ctx, block.text);
    case "hr":
      return renderHr(ctx);
    case "table":
      return renderTable(ctx, block.headers, block.rows);
  }
}

function drawPageFooters(doc: jsPDF, pageW: number, pageH: number, margin: number): void {
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    doc.setPage(p);
    // Thin separator line above the footer text.
    doc.setDrawColor(...PDF_COLORS.rule);
    doc.setLineWidth(0.25);
    doc.line(margin, pageH - FOOTER_BAND_H, pageW - margin, pageH - FOOTER_BAND_H);

    doc.setFont("Roboto", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_COLORS.muted);
    const baselineY = pageH - FOOTER_BAND_H + 6;
    doc.text("Generated by Eduarta AI", margin, baselineY);
    const label = `Page ${p} / ${total}`;
    const tw = doc.getTextWidth(label);
    doc.text(label, pageW - margin - tw, baselineY);
  }
}

function formatPdfDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ─── Markdown preprocessing ───────────────────────────────────────────────────

/** Split label-value patterns (`**Label:** value`) onto their own lines so each
 *  becomes a separate paragraph block. Handles two cases:
 *    1. Multiple `**Label:**` patterns on the same line (inline structured fields).
 *    2. `**Label:**` on a new line directly after content (already separate lines
 *       but without the blank line markdown needs for a paragraph break).
 *  We require the closing `**` to be preceded by `:` so we only split on
 *  label-like patterns, not arbitrary bold inline text. */
function preprocessMarkdown(md: string): string {
  // Insert a blank line before every `**Word:**` that is preceded by content.
  // \s* eats any whitespace including newlines so adjacent same-line and
  // single-newline cases both split.
  return md.replace(/(\S)\s+(\*\*[^*\n]+:\*\*)/g, "$1\n\n$2");
}

// ─── Frontmatter parser ───────────────────────────────────────────────────────

/** Strip a YAML-style frontmatter block (between two `---` lines) from the start
 *  of the markdown content. Returns the parsed key→value map and the remaining body.
 *  Values are kept as raw strings (no array/object parsing). */
function extractFrontmatter(md: string): { meta: Record<string, string>; body: string } {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  let i = 0;
  while (i < lines.length && (lines[i] ?? "").trim() === "") i++;
  if (i >= lines.length || (lines[i] ?? "").trim() !== "---") {
    return { meta: {}, body: md };
  }
  const meta: Record<string, string> = {};
  i++; // past opening ---
  while (i < lines.length && (lines[i] ?? "").trim() !== "---") {
    const line = lines[i] ?? "";
    const m = line.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*:\s*(.*)$/);
    // Fix 2: strip a trailing " -" artifact from field values.
    if (m && m[1] && m[2] !== undefined) meta[m[1]] = m[2].replace(/\s*-\s*$/, "").trim();
    i++;
  }
  if (i >= lines.length) return { meta: {}, body: md };
  i++; // past closing ---
  while (i < lines.length && (lines[i] ?? "").trim() === "") i++;
  return { meta, body: lines.slice(i).join("\n") };
}

// ─── Header band, title section, source info, badges ─────────────────────────

function drawHeaderBand(doc: jsPDF, pageW: number, reportLabel: string): void {
  doc.setFillColor(...PDF_COLORS.headerBg);
  doc.rect(0, 0, pageW, HEADER_BAND_H, "F");

  // "EDUARTA REPORT" subtitle (small caps via uppercase + letter-spacing).
  doc.setFont("Roboto", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...PDF_COLORS.headerSubtitle);
  drawSpacedText(doc, "EDUARTA REPORT", PAGE_MARGIN, 8.5, 0.7);

  // Report type label (white, larger).
  doc.setFont("Roboto", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...PDF_COLORS.white);
  doc.text(reportLabel, PAGE_MARGIN, 17);

  // "eduarta" wordmark on the right.
  doc.setFont("Roboto", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...PDF_COLORS.white);
  const wordmark = "eduarta";
  const tw = doc.getTextWidth(wordmark);
  doc.text(wordmark, pageW - PAGE_MARGIN - tw, 14.5);
}

function drawBadge(
  doc: jsPDF,
  x: number,
  y: number,
  label: string,
  bg: readonly [number, number, number],
  fg: readonly [number, number, number],
): number {
  const padX = 2.5;
  const padY = 1.4;
  const fontSize = 9;
  doc.setFont("Roboto", "bold");
  doc.setFontSize(fontSize);
  const tw = doc.getTextWidth(label);
  const w = tw + padX * 2;
  const h = fontSize * 0.42 + padY * 2;
  doc.setFillColor(...bg);
  doc.roundedRect(x, y, w, h, 1.2, 1.2, "F");
  doc.setTextColor(...fg);
  doc.text(label, x + padX, y + h - padY - 0.6);
  return w;
}

interface PdfTitleOptions {
  title: string;
  date: string;
  personaLabel?: string;
  reportNumber?: number;
  reportTotal?: number;
}

function drawTitleSection(
  doc: jsPDF,
  pageW: number,
  margin: number,
  startY: number,
  opts: PdfTitleOptions,
): number {
  let y = startY;

  // Title — bold black, wrapped.
  doc.setFont("Roboto", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  const wrapped = doc.splitTextToSize(opts.title, pageW - margin * 2);
  for (const line of wrapped) {
    doc.text(line, margin, y);
    y += 7;
  }

  // "Report generated: <date>" — gray, smaller.
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...PDF_COLORS.muted);
  doc.text(`Report generated: ${opts.date}`, margin, y + 1);
  y += 6;

  // Badge row.
  let bx = margin;
  if (opts.personaLabel) {
    const w = drawBadge(
      doc,
      bx,
      y,
      opts.personaLabel,
      PDF_COLORS.badgePurpleBg,
      PDF_COLORS.badgePurpleText,
    );
    bx += w + 3;
  }
  if (opts.reportNumber && opts.reportTotal) {
    const w = drawBadge(
      doc,
      bx,
      y,
      `Report ${opts.reportNumber} of ${opts.reportTotal}`,
      PDF_COLORS.badgeBlueBg,
      PDF_COLORS.badgeBlueText,
    );
    bx += w + 3;
  }
  if (opts.personaLabel || (opts.reportNumber && opts.reportTotal)) {
    y += 8;
  }

  return y + 3;
}

const SOURCE_INFO_KEYS: { key: string; label: string }[] = [
  { key: "source_file", label: "File" },
  { key: "source_duration", label: "Duration" },
  { key: "speakers_count", label: "Speakers" },
  { key: "source_language", label: "Language" },
  { key: "word_count", label: "Words" },
  { key: "source_format", label: "Format" },
];

function drawSourceInfo(
  doc: jsPDF,
  pageW: number,
  margin: number,
  startY: number,
  meta: Record<string, string>,
): number {
  let y = startY + 2;
  // Section header.
  doc.setFont("Roboto", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...PDF_COLORS.sectionHeader);
  drawSpacedText(doc, "SOURCE INFO", margin, y, 0.6);
  y += 5;

  // 2-column grid.
  const colW = (pageW - margin * 2) / 2;
  const rowH = 5;
  const rows = SOURCE_INFO_KEYS.filter((k) => {
    const v = meta[k.key];
    return v && v.toLowerCase() !== "not provided";
  });
  for (let i = 0; i < rows.length; i += 2) {
    for (let c = 0; c < 2; c++) {
      const item = rows[i + c];
      if (!item) continue;
      const value = meta[item.key] ?? "";
      const x = margin + c * colW;
      doc.setFont("Roboto", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...PDF_COLORS.muted);
      doc.text(`${item.label}:`, x, y);
      const labelW = doc.getTextWidth(`${item.label}: `);
      doc.setFont("Roboto", "normal");
      doc.setTextColor(...PDF_COLORS.text);
      const valWrapped = doc.splitTextToSize(value, colW - labelW - 2);
      doc.text(valWrapped[0] ?? value, x + labelW, y);
    }
    y += rowH;
  }
  return y + 2;
}

function drawCompactSourceRef(
  doc: jsPDF,
  pageW: number,
  margin: number,
  startY: number,
  meta: Record<string, string>,
): number {
  if (Object.keys(meta).length === 0) return startY;
  const parts: string[] = [];
  if (meta.source_file && meta.source_file.toLowerCase() !== "not provided") parts.push(meta.source_file);
  if (meta.source_duration) parts.push(meta.source_duration);
  if (meta.speakers_count) parts.push(`${meta.speakers_count} speakers`);
  if (meta.source_language) parts.push(meta.source_language);
  if (parts.length === 0) return startY;
  const y = startY + 2;
  doc.setFont("Roboto", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...PDF_COLORS.muted);
  const wrapped = doc.splitTextToSize(`From: ${parts.join(" · ")}`, pageW - margin * 2);
  let yy = y;
  for (const w of wrapped) {
    doc.text(w, margin, yy);
    yy += 5;
  }
  return yy + 1;
}

export interface PdfReportContext {
  reportType?: PromptReportType;
  reportLabel: string;       // e.g. "Summary", "Action Items"
  personaLabel?: string;     // e.g. "Business"
  reportNumber?: number;     // 1-based
  reportTotal?: number;      // total reports for this persona
  gist?: string;             // "The Gist" plain-language brief, rendered atop Summary PDFs
}

/**
 * Generate a PDF blob from markdown content using jsPDF with Cyrillic support.
 *
 * Layout: dark header band (page 1), title + date + badges, source info or
 * compact source-ref, body content with restyled headings/lists/quotes, and a
 * separator-and-page-number footer on every page.
 */
function generatePdfBlob(
  markdownContent: string,
  projectTitle: string,
  ctxOpts: PdfReportContext,
): Blob {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  registerRoboto(doc);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = PAGE_MARGIN;

  const { meta, body } = extractFrontmatter(markdownContent || "");
  const isSummary = ctxOpts.reportType === "SUMMARY";
  const titleText = meta.title || projectTitle;

  // Page 1 furniture
  drawHeaderBand(doc, pageW, ctxOpts.reportLabel);
  let y = HEADER_BAND_H + 8;
  y = drawTitleSection(doc, pageW, margin, y, {
    title: titleText,
    date: formatPdfDate(new Date()),
    personaLabel: ctxOpts.personaLabel,
    reportNumber: ctxOpts.reportNumber,
    reportTotal: ctxOpts.reportTotal,
  });

  if (isSummary && Object.keys(meta).length > 0) {
    y = drawSourceInfo(doc, pageW, margin, y, meta);
  } else {
    y = drawCompactSourceRef(doc, pageW, margin, y, meta);
  }

  const ctx: RenderCtx = {
    doc,
    margin,
    pageW,
    pageH,
    contentBottom: pageH - FOOTER_BAND_H - 4,
    baseSize: 11,
    lineGap: 5.6,
    y: y + 3,
  };

  // "The Gist" — plain-language brief above the Summary body (mirrors the UI card).
  if (isSummary && ctxOpts.gist) {
    renderGistCallout(ctx, ctxOpts.gist);
  }

  // Body — heading "##" → uppercase gray section header (renderHeading already
  // restyled). preprocessMarkdown splits inline / line-broken `**Label:**`
  // structured fields into separate paragraphs.
  // Strip trailing " -" artifacts left after empty field values (e.g. "Формат: -").
  const cleanedBody = body.replace(/ -$/gm, "");
  const blocks = parseBlocks(preprocessMarkdown(cleanedBody));
  for (const block of blocks) {
    renderBlock(ctx, block);
  }

  drawPageFooters(doc, pageW, pageH, margin);

  return doc.output("blob");
}

/**
 * Download all available reports as a ZIP file with real PDFs.
 */
export async function downloadAllAsZip(
  reports: ReportForDownload[],
  projectName: string,
  meta?: { personaLabel?: string },
): Promise<void> {
  const zip = new JSZip();
  const safeName = projectName
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .trim()
    .replace(/\s+/g, "_") || "report";

  // Position within the persona's report set (excluding transcript) is used
  // for the "Report N of M" badge.
  const nonTranscript = reports.filter((r) => r.type !== "TRANSCRIPT");
  const indexByType = new Map(nonTranscript.map((r, i) => [r.type, i + 1]));
  const total = nonTranscript.length;

  for (const report of reports) {
    const fileBase = `${safeName}_${report.label.replace(/\s+/g, "_")}`;
    if (report.type === "TRANSCRIPT") {
      zip.file(`${fileBase}.txt`, stripMarkdown(report.content));
    } else {
      const pdfBlob = generatePdfBlob(report.content, projectName, {
        reportType: report.type,
        reportLabel: report.label,
        personaLabel: meta?.personaLabel,
        reportNumber: indexByType.get(report.type),
        reportTotal: total || undefined,
      });
      zip.file(`${fileBase}.pdf`, pdfBlob);
    }
  }

  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, `${safeName}_reports.zip`);
}

/**
 * Get available download formats for a given report type.
 */
export function getFormatsForType(
  type: PromptReportType,
): { format: DownloadFormat; label: string; enabled: boolean }[] {
  if (type === "TRANSCRIPT") {
    // Generated server-side from transcript elements (enriched speaker names,
    // real timestamps). Routed through the /jobs/{id}/subtitles/ endpoint, not
    // the client-side downloadReport path.
    return [
      { format: "srt", label: "SRT", enabled: true },
      { format: "vtt", label: "VTT", enabled: true },
      { format: "ass", label: "ASS", enabled: true },
      { format: "txt", label: "TXT", enabled: true },
    ];
  }
  return [
    { format: "pdf", label: "PDF", enabled: true },
    { format: "docx", label: "DOCX", enabled: true },
  ];
}

/**
 * Download a single report in the specified format.
 *
 * `pdfMeta` provides the rich context needed for the styled PDF (project title,
 * report type/label, persona label, report number/total). When omitted, the
 * function falls back to a minimal PDF using `title` as both project title and
 * report label.
 */
export function downloadReport(
  content: string,
  title: string,
  filename: string,
  format: DownloadFormat,
  pdfMeta?: { projectTitle: string } & PdfReportContext,
): void {
  switch (format) {
    case "pdf":
      // Use the same nicely-formatted jsPDF renderer as the ZIP path.
      try {
        const projectTitle = pdfMeta?.projectTitle ?? title;
        const ctx: PdfReportContext = pdfMeta ?? { reportLabel: title };
        const blob = generatePdfBlob(content, projectTitle, ctx);
        triggerDownload(blob, filename + ".pdf");
      } catch (err) {
        console.error("PDF generation failed, falling back to print dialog", err);
        downloadAsPdf(content, title);
      }
      break;
    case "txt":
      downloadAsText(stripMarkdown(content), filename + ".txt");
      break;
    case "srt":
      downloadAsSrt(content, filename + ".srt");
      break;
    case "md":
      downloadAsMarkdown(content, filename + ".md");
      break;
    case "docx":
      // Not implemented yet
      break;
  }
}
